import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardCheckIcon,
  InformationCircleIcon,
  LocationMarkerIcon,
  PaperAirplaneIcon,
  ShieldCheckIcon,
} from "@heroicons/react/outline";
import SharedJobPhotoListing from "./SharedPhotoListing";
import * as api from "./apiService";
import Loader from "../../components/loader/Loader";
import AnimatedPage from "../../components/animatedPage/AnimatedPage";
import logo from "../../images/logo_red-no-text.png";

const STATUS = {
  U: {
    label: "Submitted",
    styles: "bg-indigo-50 text-indigo-700 ring-indigo-200",
  },
  A: { label: "Received", styles: "bg-blue-50 text-blue-700 ring-blue-200" },
  S: { label: "Assigned", styles: "bg-amber-50 text-amber-700 ring-amber-200" },
  W: {
    label: "In progress",
    styles: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  },
  R: {
    label: "Review",
    styles: "bg-purple-50 text-purple-700 ring-purple-200",
  },
  C: {
    label: "Complete",
    styles: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  },
  I: { label: "Invoiced", styles: "bg-blue-50 text-blue-700 ring-blue-200" },
  T: {
    label: "Canceled",
    styles: "bg-slate-100 text-slate-700 ring-slate-200",
  },
};

const SERVICE_STATUS = {
  A: "Received",
  S: "Assigned",
  W: "In progress",
  R: "Review",
  C: "Included",
  I: "Included",
  T: "Canceled",
};

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function formatLocalDateTime(value, { includeYear = false } = {}) {
  if (!value) return "Not specified";

  const match = String(value)
    .trim()
    .match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})[,\s]+(\d{1,2}):(\d{2})(?:\s*(AM|PM))?(?:\s*(LT))?$/i,
    );
  if (!match) return value;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = match[3].length === 2 ? `20${match[3]}` : match[3];
  let hour = Number(match[4]);
  let meridiem = match[6]?.toUpperCase();

  if (!meridiem) {
    meridiem = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
  }

  const date = `${MONTH_NAMES[month - 1] || match[1]} ${day}${
    includeYear ? `, ${year}` : ""
  }`;
  const localTime = match[7] ? " LT" : "";
  return `${date} · ${hour}:${match[5]} ${meridiem}${localTime}`;
}

function formatCommentDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
    .format(date)
    .replace(",", " ·");
}

function getRequestedBy(job) {
  if (job.requested_by) return job.requested_by;
  const name = [job.created_by?.first_name, job.created_by?.last_name]
    .filter(Boolean)
    .join(" ");
  return name || "Not provided";
}

function getCommentAuthor(comment) {
  const authorName = [comment.author?.first_name, comment.author?.last_name]
    .filter(Boolean)
    .join(" ");
  const integrationName = [
    comment.integration_first_name,
    comment.integration_last_name,
  ]
    .filter(Boolean)
    .join(" ");

  return authorName || integrationName || "LiveTakeoff Operations";
}

function CardHeader({ icon: Icon, iconStyles, title, description, trailing }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-4 py-4 sm:px-5">
      <div className="flex min-w-0 items-start gap-3">
        <span
          className={classNames(
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            iconStyles,
          )}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 className="text-sm font-extrabold text-slate-950 sm:text-base">
            {title}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">{description}</p>
        </div>
      </div>
      {trailing}
    </div>
  );
}

function Detail({ label, children, className = "" }) {
  return (
    <div className={className}>
      <dt className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm font-bold leading-5 text-slate-800">
        {children || "Not specified"}
      </dd>
    </div>
  );
}

function OperationalSummary({ job }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <CardHeader
        icon={LocationMarkerIcon}
        iconStyles="bg-red-50 text-[#E3222A]"
        title="Operational summary"
        description="Where and when the service was completed"
      />
      <dl className="p-4 sm:p-5">
        <div className="grid gap-5 border-b border-slate-100 pb-5 sm:grid-cols-2">
          <Detail label="Airport">
            {job.airport?.name || "Not specified"}
          </Detail>
          <Detail label="FBO">{job.fbo?.name || "Not specified"}</Detail>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-5 pt-5 lg:grid-cols-4">
          <Detail label="Arrival">
            {job.on_site
              ? "On site"
              : formatLocalDateTime(
                  job.arrival_formatted_date || job.estimatedETA,
                )}
          </Detail>
          <Detail label="Departure">
            {formatLocalDateTime(
              job.departure_formatted_date || job.estimatedETD,
            )}
          </Detail>
          <Detail label="Complete by">
            {formatLocalDateTime(
              job.complete_before_formatted_date || job.completeBy,
            )}
          </Detail>
          <Detail label="Completed">
            {formatLocalDateTime(job.completion_date)}
          </Detail>
        </div>
      </dl>
    </section>
  );
}

function JobInformation({ job }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <CardHeader
        icon={PaperAirplaneIcon}
        iconStyles="bg-sky-50 text-sky-600"
        title="Job information"
        description="Public closeout details"
      />
      <dl className="divide-y divide-slate-100 px-4 sm:px-5">
        <Detail label="Aircraft" className="py-3.5">
          {job.aircraftType?.name || "Not specified"}
        </Detail>
        <Detail label="Customer PO" className="py-3.5">
          {job.customer_purchase_order || "Not provided"}
        </Detail>
        <Detail label="Requested by" className="py-3.5">
          {getRequestedBy(job)}
        </Detail>
        <Detail label="Request date" className="py-3.5">
          {formatLocalDateTime(job.requestDate, { includeYear: true })}
        </Detail>
      </dl>
    </section>
  );
}

function CompletedServices({ services }) {
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleScope = (serviceKey) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(serviceKey)) next.delete(serviceKey);
      else next.add(serviceKey);
      return next;
    });
  };

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <CardHeader
        icon={ClipboardCheckIcon}
        iconStyles="bg-emerald-50 text-emerald-600"
        title="Completed services"
        description="Scope included in this closeout"
        trailing={
          <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
            {services.length} {services.length === 1 ? "service" : "services"}
          </span>
        }
      />

      {services.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-500">
          No services were included in this closeout.
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {services.map((service) => {
            const isCompleted = ["C", "I"].includes(service.status);
            const scopeItems = (service.checklist_actions || [])
              .map((action) =>
                String(action.name || "").replace(/^\s*-\s*/, ""),
              )
              .filter(Boolean);
            const scopeDescription = String(
              service.scope ?? (service.description || service.short_description || ""),
            ).trim();
            const descriptionItems = scopeDescription
              .split(";")
              .map((item) => item.trim())
              .filter(Boolean);
            const displayedScopeItems =
              descriptionItems.length > 1
                ? descriptionItems
                : scopeDescription || service.scope !== undefined
                  ? []
                  : scopeItems;
            const hasScope = displayedScopeItems.length > 0 || scopeDescription;
            const isExpanded = expanded.has(service.key);

            return (
              <article key={service.key} className="px-4 py-4 sm:px-5">
                <div className="flex items-start gap-3">
                  {isCompleted && (
                    <span
                      data-testid="completed-service-check"
                      className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"
                    >
                      <CheckIcon className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-sm font-bold text-slate-900">
                        {service.name}
                      </p>
                      <span className="shrink-0 text-xs font-semibold text-emerald-700">
                        {SERVICE_STATUS[service.status] || "Included"}
                      </span>
                    </div>
                    {hasScope && (
                      <button
                        type="button"
                        onClick={() => toggleScope(service.key)}
                        aria-expanded={isExpanded}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0284C7] transition hover:text-sky-700"
                      >
                        {isExpanded ? "Hide scope" : "View scope"}
                        {isExpanded ? (
                          <ChevronUpIcon
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        ) : (
                          <ChevronDownIcon
                            className="h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                {hasScope && isExpanded && (
                  <div className="ml-9 mt-3 rounded-lg bg-sky-50 px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-sky-700">
                      Scope of work
                    </p>
                    {displayedScopeItems.length > 0 ? (
                      <ul
                        data-testid="service-scope-list"
                        className="mt-2 grid gap-x-8 gap-y-2 text-sm leading-5 text-slate-700 sm:grid-cols-2"
                      >
                        {displayedScopeItems.map((item, index) => (
                          <li
                            key={`${service.key}-scope-${index}`}
                            className="flex items-start gap-2"
                          >
                            <span
                              aria-hidden="true"
                              className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-500"
                            />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-1.5 whitespace-pre-line text-sm leading-6 text-slate-700">
                        {scopeDescription}
                      </p>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function PublicComments({ comments }) {
  const publicComments = comments.filter(
    (comment) => comment.is_public !== false,
  );

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <CardHeader
        icon={InformationCircleIcon}
        iconStyles="bg-blue-50 text-blue-600"
        title="Public notes"
        description="Updates shared with this closeout"
        trailing={
          <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
            {publicComments.length}{" "}
            {publicComments.length === 1 ? "comment" : "comments"}
          </span>
        }
      />

      {publicComments.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-slate-500">
          No public notes were shared with this closeout.
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {publicComments.map((comment) => (
            <article key={comment.id} className="px-4 py-4 sm:px-5">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <p className="text-sm font-bold text-slate-900">
                  {getCommentAuthor(comment)}
                </p>
                <span className="text-xs text-slate-400">
                  {formatCommentDate(comment.created)}
                </span>
              </div>
              {comment.comment_html ? (
                <div
                  className="mt-2 text-sm leading-6 text-slate-700"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(comment.comment_html),
                  }}
                />
              ) : (
                <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-700">
                  {comment.comment}
                </p>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default function SharedJob() {
  const { encoded_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [jobDetails, setJobDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let active = true;

    const getJobDetails = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const { data } = await api.getJobDetails(encoded_id);
        if (active) setJobDetails(data);
      } catch (error) {
        if (active) {
          setErrorMessage(
            "This public closeout is unavailable. Check the link and try again.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    getJobDetails();
    return () => {
      active = false;
    };
  }, [encoded_id]);

  const services = useMemo(() => {
    if (!jobDetails) return [];
    return [
      ...(jobDetails.service_assignments || []).map((service, index) => ({
        ...service,
        key: `service-${service.id ?? index}`,
      })),
      ...(jobDetails.retainer_service_assignments || []).map(
        (service, index) => ({
          ...service,
          key: `retainer-${service.id ?? index}`,
        }),
      ),
    ];
  }, [jobDetails]);

  if (loading) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Loader />
      </div>
    );
  }

  if (errorMessage || !jobDetails) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
        <h1 className="text-xl font-extrabold text-slate-950">
          Closeout unavailable
        </h1>
        <p className="mt-2 text-sm text-slate-600">{errorMessage}</p>
        <Link
          to="/shared/contact"
          className="mt-5 inline-flex rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
        >
          Contact us
        </Link>
      </main>
    );
  }

  const status = STATUS[jobDetails.status] || STATUS.U;
  const completionLabel = jobDetails.completion_date
    ? formatLocalDateTime(jobDetails.completion_date, { includeYear: true })
    : status.label;

  return (
    <AnimatedPage>
      <div className="shared-closeout-redesign text-slate-700">
        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
            <ShieldCheckIcon
              className="h-4 w-4 text-emerald-600"
              aria-hidden="true"
            />
            <span>Public job closeout</span>
            <span aria-hidden="true">·</span>
            <span>No sign-in required</span>
          </div>

          <section className="mt-3 overflow-hidden rounded-xl border border-slate-200 border-l-4 border-l-emerald-500 bg-white shadow-sm">
            <div className="grid gap-5 p-5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:p-6">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h1 className="text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">
                    {jobDetails.tailNumber}
                  </h1>
                  <span
                    className={classNames(
                      "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset",
                      status.styles,
                    )}
                  >
                    {status.label}
                  </span>
                </div>
                <p className="mt-1 text-sm font-medium text-slate-600">
                  {jobDetails.customer?.name || "Customer not specified"}
                </p>
                {jobDetails.tags?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {jobDetails.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="rounded-md bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200"
                      >
                        {tag.tag_short_name || tag.tag_name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-emerald-50 px-4 py-3 sm:min-w-[220px]">
                <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-700">
                  {jobDetails.completion_date
                    ? "Closeout completed"
                    : "Current status"}
                </p>
                <p className="mt-1 text-sm font-extrabold text-emerald-950">
                  {completionLabel}
                </p>
              </div>
            </div>
          </section>

          <div
            data-testid="closeout-summary-grid"
            className="mt-5 grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1.65fr)_minmax(300px,0.85fr)]"
          >
            <OperationalSummary job={jobDetails} />
            <JobInformation job={jobDetails} />
          </div>

          <div className="mt-5 space-y-5">
            <CompletedServices services={services} />
            <PublicComments comments={jobDetails.job_comments || []} />
            <SharedJobPhotoListing
              photos={jobDetails.job_photos || []}
              purchase_order={jobDetails.purchase_order}
            />

            <div className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <span className="inline-flex items-center gap-2">
                <ShieldCheckIcon
                  className="h-4 w-4 text-emerald-600"
                  aria-hidden="true"
                />
                This closeout contains public job information only.
              </span>
            </div>
          </div>
        </main>

        <footer className="mt-8 border-t border-slate-200 bg-white">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-6 sm:px-6 lg:px-8">
            <Link to="/" className="flex items-center gap-2">
              <img className="h-7 w-7 object-contain" src={logo} alt="" />
              <span className="text-sm font-extrabold text-slate-950">
                LiveTakeoff
              </span>
              <span className="sr-only">home</span>
            </Link>
            <p className="text-right text-xs text-slate-400">
              © {new Date().getFullYear()} LiveTakeoff. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </AnimatedPage>
  );
}
